import { useEffect, useRef } from 'react';
import { endLessonViewSession, startLessonViewSession } from '@busy/api';

// Lesson view session lifecycle hook (migration 0069).
//
// Opens a lesson_view_sessions row when (lessonId, isOpen) goes
// (truthy, true). Closes the same row on unmount / on the params
// going (falsy, false). Idempotent on the session id; rapid expand/
// collapse cycles produce one row per (open, close) pair.
//
// `maxScrollPct` is captured as a ref so the caller can update it
// during the session (e.g. via a scroll listener) without re-running
// the effect. On close, we send whatever value is currently in the
// ref. Default 0 means "no scroll tracked" — the caller can omit
// scroll wiring and we still get a useful (start, end) pair for
// engagement-depth analysis.

export function useLessonViewSession(
  lessonId: string | null | undefined,
  isOpen: boolean,
  options: { maxScrollPctRef?: React.MutableRefObject<number> } = {},
) {
  const sessionIdRef = useRef<string | null>(null);
  // Track the lessonId at session-start time so a change to lessonId
  // mid-session closes the OLD session before opening a new one.
  const openLessonIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!lessonId || !isOpen) return;

    let cancelled = false;
    let createdSessionId: string | null = null;

    (async () => {
      const sid = await startLessonViewSession({ lessonId });
      if (cancelled) {
        // Race: closed before the insert returned. End the orphan
        // immediately so we don't leave a row with no ended_at.
        const scrollPct = options.maxScrollPctRef?.current ?? 0;
        void endLessonViewSession({
          clientSessionId: sid,
          maxScrollPct: scrollPct,
        });
        return;
      }
      createdSessionId = sid;
      sessionIdRef.current = sid;
      openLessonIdRef.current = lessonId;
    })();

    return () => {
      cancelled = true;
      const sid = createdSessionId ?? sessionIdRef.current;
      if (sid) {
        const scrollPct = options.maxScrollPctRef?.current ?? 0;
        void endLessonViewSession({
          clientSessionId: sid,
          maxScrollPct: scrollPct,
        });
        sessionIdRef.current = null;
        openLessonIdRef.current = null;
      }
    };
  }, [lessonId, isOpen, options.maxScrollPctRef]);
}
